import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  SFSlideThumbnail,
  SFTypography,
  SFStack,
  SFHelpers,
  SFUseStyled,
  SFIcons,
  SFToggleButtons,
  SFIconButton,
} from "@sf-core";
import getStatusBadge from "../../constants/statusBadgeMappings";
import { getCourseUrl } from "../../constants/urls";
import { useDispatch } from "react-redux";
import { userConfigurationActions } from "../../redux/reducers/userConfigurationReducer";
import { saveCourseTrack } from "../../services/courseService";
import CourseDetailsButton from "../../../core/components/icons/courseDetailsButton";
import CourseDetailsDialog from "./courseDetailsDialog";

const { styled } = SFUseStyled();

const CourseDetailStackStyled = styled(SFStack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  position: "relative",
  "& .course-details-button-container": {
    display: "none",
  },
  "&:hover .course-details-button-container": {
    display: "flex",
  },
}));

const CourseContainerStackStyled = styled(SFStack)(({ theme }) => ({
  "&:focus-within .course-details-button-container": {
    display: "flex",
  },
}));

const StyledButtonStack = styled(SFStack)(({ theme }) => ({
  borderRadius: "0px 6px 6px 0px",
  background: "rgba(50,50,60, 0.65)",
  position: "absolute",
  width: "60px",
  height: "100%",
  right: 0,
  top: theme.spacing(-1.4),
}));

const StyledTypography = styled(SFTypography, {
  shouldForwardProp: (prop) => prop !== "lines",
})(({ theme, lines }) => ({
  overflow: "hidden",
  wordWrap: "break-word",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: `calc(${lines})`,
  lineClamp: `calc(${lines})`,
  WebkitBoxOrient: "vertical",
  p: {
    margin: 0,
  },
}));

const CourseItem = ({
  course,
  skillPathLevelId,
  onClick,
  descriptionLines,
  preview = false,
  previewId = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  let { id } = useParams();
  id = previewId ?? id.toLowerCase();
  const [firstRender, setFirstRender] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [showCourseDetailsDialog, setShowCourseDetailsDialog] = useState(false);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }

    if (loaded) return;

    const containerElement = document.querySelector("#course-container");
    const targetElement = document.querySelector(`#course-${course.id}`);

    if (!containerElement || !targetElement) return;

    const options = {
      root: containerElement,
      rootMargin: "20px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((elements) => {
      if (elements[0].isIntersecting) {
        setLoaded(true);
      }
    }, options);

    observer.observe(targetElement);

    return () => observer.unobserve(targetElement);
  }, [firstRender, loaded]);

  const handleThumbnailClick = () => {
    if (course.locked || preview) return;
    dispatch(
      userConfigurationActions.setLastAccessed({
        domainId: id,
        skillPathLevelId,
        skillPathId: course.skillPathId,
      })
    );
    onClick && onClick();
    let data = {
      skillPathId: course.skillPathId,
      contentId: course.id,
    };
    saveCourseTrack(data);
    navigate(getCourseUrl(course.id), {
      state: { previousLoc: location.pathname },
    });
  };

  return (
    <>
      <CourseDetailsDialog
        course={course}
        showCourseDetailsDialog={showCourseDetailsDialog}
        setShowCourseDetailsDialog={setShowCourseDetailsDialog}
        handleThumbnailClick={handleThumbnailClick}
      />
      <CourseContainerStackStyled
        direction="row"
        spacing={2}
        id={`course-${course.id}`}
      >
        {loaded && (
          <>
            <SFSlideThumbnail
              className="video-button"
              onClick={handleThumbnailClick}
              image={course.thumbnail}
              // show status label in relation to the course status(Not started, In progress, Completed)
              badge={getStatusBadge(course.status, t)}
              icon={
                !preview ? (
                  course.locked ? (
                    <SFIcons.SFLockIcon size="large" color="grey200" />
                  ) : (
                    <SFIcons.SFPlayIcon size="xlarge" color="grey200" />
                  )
                ) : null
              }
            />
            <SFStack>
              <CourseDetailStackStyled spacing={1.5}>
                <StyledTypography
                  variant="courseCarouselTitle"
                  lines={2}
                  component="h2"
                >
                  {course.name}
                </StyledTypography>
                <StyledTypography variant="body1" lines={descriptionLines}>
                  {course.summary ? course.summary : course.description}
                </StyledTypography>
                {course.skills?.length > 0 && (
                  <SFToggleButtons
                    size="small"
                    disabled={true}
                    haveCollapsibleItems={true}
                    key={course.id}
                    items={course.skills.map((x) => ({
                      value: x.id,
                      label: x.name,
                    }))}
                    noHover
                  />
                )}
                {!preview && (
                  <StyledButtonStack
                    alignItems="center"
                    justifyContent="center"
                    className="course-details-button-container"
                  >
                    <SFIconButton
                      onClick={() => setShowCourseDetailsDialog(true)}
                      variant="transparent"
                    >
                      <CourseDetailsButton />
                    </SFIconButton>
                  </StyledButtonStack>
                )}
              </CourseDetailStackStyled>
              <SFTypography variant="body1">
                {t("lxpComponents.courses.coursesDuration", {
                  duration: SFHelpers.DurationFormatter(
                    course.duration,
                    SFHelpers.DurationFormatterOptions.ALL_UNITS
                  ),
                })}
              </SFTypography>
            </SFStack>
          </>
        )}
      </CourseContainerStackStyled>
    </>
  );
};

export default CourseItem;
